/**
 * @fileoverview Master Key Encryption Service - Manages master encryption keys for WebAuthn
 *
 * This service implements the master key architecture where:
 * - A single master AES-256 key encrypts all user data
 * - Master key is protected by WebAuthn credentials (biometric)
 * - Each primary device stores an encrypted copy of the master key
 * - Supports multiple primary devices (2-of-M recovery model)
 * - 24-word recovery key as backup access method
 *
 * **Architecture:**
 * - Master Key: Random AES-256 key generated once
 * - Protection: Encrypted with WebAuthn credential OR recovery key
 * - Storage: Encrypted copy stored with each WebAuthn credential
 * - Access: Biometric unlock OR recovery key entry
 *
 * **Key Flow:**
 * 1. Generate master key → generateMasterKey()
 * 2. Encrypt with WebAuthn → encryptMasterKeyWithWebAuthn()
 * 3. Store encrypted key → (stored in webauthn_credentials table)
 * 4. Later: Unlock with biometric → decryptMasterKeyWithWebAuthn()
 * 5. Emergency: Recover with 24-word phrase → recoverMasterKeyWithRecoveryKey()
 *
 * @module services/EncryptionService
 * @requires @xanectech/lib
 */

import { createLogger } from '@xanectech/lib';
import { entropyToMnemonic, validateMnemonic } from '../utils/bip39Utils';

const logger = createLogger('EncryptionService');

// ============================================================================
// Types and Interfaces
// ============================================================================

export interface MasterKey {
  key: CryptoKey;
  keyMaterial: Uint8Array; // For storage/transmission
}

export interface EncryptedMasterKey {
  encryptedKey: string; // Base64-encoded encrypted key
  iv: string; // Base64-encoded initialization vector
  salt: string; // Base64-encoded salt
  algorithm: string;
}

export interface RecoveryKey {
  words: string[]; // 24-word BIP39 mnemonic
  hash: string; // SHA-256 hash for validation
}

// ============================================================================
// Master Key Generation
// ============================================================================

/**
 * Generate a new master encryption key
 *
 * Creates a random AES-256-GCM key that will be used to encrypt all user data.
 * This key is generated once during initial setup and protected by WebAuthn
 * credentials and/or recovery keys.
 *
 * @returns Master key and raw key material
 */
export async function generateMasterKey(): Promise<MasterKey> {

  try {
    if (!window.crypto || !window.crypto.subtle) {
      throw new Error('Web Crypto API not supported');
    }

    // Generate AES-256-GCM key
    const key = await window.crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256,
      },
      true, // extractable
      ['encrypt', 'decrypt'],
    );

    // Export raw key material for storage
    const exportedKey = await window.crypto.subtle.exportKey('raw', key);
    const keyMaterial = new Uint8Array(exportedKey);

    return { key, keyMaterial };
  } catch (error) {
    logger.error('Failed to generate master key', error as Error);
    throw new Error('Failed to generate master encryption key');
  }
}

/**
 * Import master key from raw material
 *
 * @param keyMaterial - Raw key bytes
 * @returns Imported CryptoKey
 */
export async function importMasterKey(
  keyMaterial: Uint8Array,
): Promise<CryptoKey> {

  try {
    // Ensure keyMaterial is backed by a regular ArrayBuffer, not SharedArrayBuffer
    const buffer = new ArrayBuffer(keyMaterial.length);
    new Uint8Array(buffer).set(keyMaterial);

    const key = await window.crypto.subtle.importKey(
      'raw',
      buffer,
      {
        name: 'AES-GCM',
        length: 256,
      },
      true,
      ['encrypt', 'decrypt'],
    );

    return key;
  } catch (error) {
    logger.error('Failed to import master key', error as Error);
    throw new Error('Failed to import master encryption key');
  }
}

// ============================================================================
// Master Key Protection with WebAuthn
// ============================================================================

/**
 * Encrypt master key with WebAuthn-derived encryption key
 *
 * The WebAuthn credential is used to derive an encryption key that protects
 * the master key. This ties the master key to the user's biometric/device.
 *
 * NOTE: Actual WebAuthn integration happens in edge function for security.
 * This is a client-side helper that prepares data for the edge function.
 *
 * @param masterKeyMaterial - Raw master key bytes
 * @param password - User password for key derivation (temporary MVP)
 * @returns Encrypted master key bundle
 */
export async function encryptMasterKeyWithPassword(
  masterKeyMaterial: Uint8Array,
  password: string,
): Promise<EncryptedMasterKey> {

  try {
    // Generate random salt
    const salt = window.crypto.getRandomValues(new Uint8Array(16));

    // Derive key from password using PBKDF2
    const passwordKey = await deriveKeyFromPassword(password, salt);

    // Generate random IV backed by a regular ArrayBuffer
    const ivBuffer = new ArrayBuffer(12);
    const iv = new Uint8Array(ivBuffer);
    window.crypto.getRandomValues(iv);

    // Encrypt master key
    // Ensure masterKeyMaterial is backed by a regular ArrayBuffer
    const keyBuffer = new ArrayBuffer(masterKeyMaterial.length);
    new Uint8Array(keyBuffer).set(masterKeyMaterial);

    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      passwordKey,
      keyBuffer,
    );


    return {
      encryptedKey: arrayBufferToBase64(encryptedBuffer),
      iv: arrayBufferToBase64(iv),
      salt: arrayBufferToBase64(salt),
      algorithm: 'AES-256-GCM',
    };
  } catch (error) {
    logger.error('Failed to encrypt master key', error as Error);
    throw new Error('Failed to encrypt master key');
  }
}

/**
 * Decrypt master key with password
 *
 * @param encryptedMasterKey - Encrypted master key bundle
 * @param password - User password
 * @returns Decrypted master key material
 */
export async function decryptMasterKeyWithPassword(
  encryptedMasterKey: EncryptedMasterKey,
  password: string,
): Promise<Uint8Array> {

  try {
    // Derive key from password
    const salt = base64ToArrayBuffer(encryptedMasterKey.salt);
    const passwordKey = await deriveKeyFromPassword(password, salt);

    // Decrypt master key
    const iv = base64ToArrayBuffer(encryptedMasterKey.iv);
    const encryptedDataArray = base64ToArrayBuffer(
      encryptedMasterKey.encryptedKey,
    );

    // Ensure encryptedData is backed by a regular ArrayBuffer, not SharedArrayBuffer
    const encryptedBuffer = new ArrayBuffer(encryptedDataArray.length);
    new Uint8Array(encryptedBuffer).set(encryptedDataArray);

    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: new Uint8Array(iv),
      },
      passwordKey,
      new Uint8Array(encryptedBuffer),
    );

    return new Uint8Array(decryptedBuffer);
  } catch (error) {
    logger.error('Failed to decrypt master key', error as Error);
    throw new Error('Failed to decrypt master key - invalid password');
  }
}

// ============================================================================
// Recovery Key Generation
// ============================================================================

/**
 * Generate a 24-word recovery key (BIP39-style mnemonic)
 *
 * This recovery key can be used to recover the master encryption key if all
 * WebAuthn credentials are lost. Users should store this offline securely.
 *
 * @returns 24-word recovery phrase and validation hash
 */
export async function generateRecoveryKey(): Promise<RecoveryKey> {

  try {
    // Generate 32 bytes of random data (256 bits → 24 BIP39 words)
    const entropy = window.crypto.getRandomValues(new Uint8Array(32));

    // Convert to 24-word mnemonic using the standard BIP39 algorithm
    const mnemonic = entropyToMnemonic(entropy);
    const words = mnemonic.split(' ');

    // Generate validation hash
    const hash = await generateRecoveryKeyHash(mnemonic);

    return { words, hash };
  } catch (error) {
    logger.error('Failed to generate recovery key', error as Error);
    throw new Error('Failed to generate recovery key');
  }
}

/**
 * Validate a 24-word BIP39 recovery phrase
 *
 * @param recoveryPhrase - Space-separated 24-word recovery phrase
 * @returns `true` if the phrase is a valid BIP39 mnemonic, `false` otherwise
 */
export function validateRecoveryKey(recoveryPhrase: string): boolean {
  return validateMnemonic(recoveryPhrase);
}

/**
 * Encrypt master key with recovery key
 *
 * @param masterKeyMaterial - Raw master key bytes
 * @param recoveryPhrase - 24-word recovery phrase
 * @returns Encrypted master key bundle
 */
export async function encryptMasterKeyWithRecoveryKey(
  masterKeyMaterial: Uint8Array,
  recoveryPhrase: string,
): Promise<EncryptedMasterKey> {

  try {
    // Use recovery phrase as password input
    return await encryptMasterKeyWithPassword(
      masterKeyMaterial,
      recoveryPhrase,
    );
  } catch (error) {
    logger.error(
      'Failed to encrypt master key with recovery key',
      error as Error,
    );
    throw new Error('Failed to encrypt master key with recovery key');
  }
}

/**
 * Decrypt master key with recovery key
 *
 * @param encryptedMasterKey - Encrypted master key bundle
 * @param recoveryPhrase - 24-word recovery phrase
 * @returns Decrypted master key material
 */
export async function decryptMasterKeyWithRecoveryKey(
  encryptedMasterKey: EncryptedMasterKey,
  recoveryPhrase: string,
): Promise<Uint8Array> {

  try {
    return await decryptMasterKeyWithPassword(
      encryptedMasterKey,
      recoveryPhrase,
    );
  } catch (error) {
    logger.error(
      'Failed to decrypt master key with recovery key',
      error as Error,
    );
    throw new Error('Failed to decrypt master key - invalid recovery key');
  }
}

// ============================================================================
// Data Encryption with Master Key
// ============================================================================

/**
 * Encrypt data with master key
 *
 * @param masterKey - Master encryption key
 * @param data - Data to encrypt (string or object)
 * @returns Encrypted data bundle
 */
export async function encryptDataWithMasterKey(
  masterKey: CryptoKey,
  data: string | object,
): Promise<{ encryptedData: string; iv: string }> {

  try {
    // Convert data to bytes
    const dataString = typeof data === 'string' ? data : JSON.stringify(data);
    const dataBytes = new TextEncoder().encode(dataString);

    // Generate random IV
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    // Encrypt
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      masterKey,
      dataBytes,
    );

    return {
      encryptedData: arrayBufferToBase64(encryptedBuffer),
      iv: arrayBufferToBase64(iv),
    };
  } catch (error) {
    logger.error('Failed to encrypt data', error as Error);
    throw new Error('Failed to encrypt data with master key');
  }
}

/**
 * Decrypt data with master key
 *
 * @param masterKey - Master encryption key
 * @param encryptedData - Encrypted data (base64)
 * @param iv - Initialization vector (base64)
 * @returns Decrypted data string
 */
export async function decryptDataWithMasterKey(
  masterKey: CryptoKey,
  encryptedData: string,
  iv: string,
): Promise<string> {

  try {
    const dataArray = base64ToArrayBuffer(encryptedData);
    const ivArray = base64ToArrayBuffer(iv);

    // Ensure data buffer is backed by a regular ArrayBuffer, not SharedArrayBuffer
    const dataBuffer = new ArrayBuffer(dataArray.length);
    new Uint8Array(dataBuffer).set(dataArray);

    // Ensure IV is backed by a regular ArrayBuffer, not SharedArrayBuffer
    const ivBuffer = new ArrayBuffer(ivArray.length);
    new Uint8Array(ivBuffer).set(ivArray);

    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: new Uint8Array(ivBuffer),
      },
      masterKey,
      new Uint8Array(dataBuffer),
    );

    const decryptedString = new TextDecoder().decode(decryptedBuffer);
    return decryptedString;
  } catch (error) {
    logger.error('Failed to decrypt data', error as Error);
    throw new Error('Failed to decrypt data with master key');
  }
}

// ============================================================================
// Document Encryption (Binary Data)
// ============================================================================

/**
 * Generate a random AES-256 key for encrypting documents
 *
 * Each document gets its own unique encryption key, which is then wrapped
 * with the user's master key. This provides an additional security layer.
 *
 * @returns Generated document encryption key
 */
export async function generateDocumentKey(): Promise<CryptoKey> {

  try {
    const key = await window.crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256,
      },
      true, // extractable - needed to wrap with master key
      ['encrypt', 'decrypt'],
    );

    return key;
  } catch (error) {
    logger.error('Failed to generate document key', error as Error);
    throw new Error('Failed to generate document key');
  }
}

/**
 * Encrypt binary file data with an encryption key
 *
 * Encrypts large binary data (files) using AES-256-GCM.
 * Each encryption uses a random IV.
 *
 * @param fileData - Binary data to encrypt (ArrayBuffer)
 * @param key - Encryption key (CryptoKey)
 * @returns Encrypted data (ArrayBuffer) and IV (base64-encoded)
 */
export async function encryptFileBinary(
  fileData: ArrayBuffer,
  key: CryptoKey,
): Promise<{ encryptedData: ArrayBuffer; iv: string }> {

  try {
    // Generate random IV
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    // Ensure data is backed by regular ArrayBuffer
    const dataBuffer = new ArrayBuffer(fileData.byteLength);
    new Uint8Array(dataBuffer).set(new Uint8Array(fileData));

    // Encrypt
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      key,
      dataBuffer,
    );


    return {
      encryptedData: encryptedBuffer,
      iv: arrayBufferToBase64(iv),
    };
  } catch (error) {
    logger.error('Failed to encrypt file data', error as Error);
    throw new Error('Failed to encrypt file');
  }
}

/**
 * Decrypt binary file data with an encryption key
 *
 * @param encryptedData - Encrypted data (ArrayBuffer)
 * @param iv - Initialization vector (base64-encoded)
 * @param key - Decryption key (CryptoKey)
 * @returns Decrypted binary data (ArrayBuffer)
 */
export async function decryptFileBinary(
  encryptedData: ArrayBuffer,
  iv: string,
  key: CryptoKey,
): Promise<ArrayBuffer> {

  try {
    const ivArray = base64ToArrayBuffer(iv);

    // Ensure buffers are backed by regular ArrayBuffer
    const dataBuffer = new ArrayBuffer(encryptedData.byteLength);
    new Uint8Array(dataBuffer).set(new Uint8Array(encryptedData));

    const ivBuffer = new ArrayBuffer(ivArray.length);
    new Uint8Array(ivBuffer).set(ivArray);

    // Decrypt
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: new Uint8Array(ivBuffer),
      },
      key,
      new Uint8Array(dataBuffer),
    );

    return decryptedBuffer;
  } catch (error) {
    logger.error('Failed to decrypt file data', error as Error);
    throw new Error('Failed to decrypt file');
  }
}

/**
 * Wrap (encrypt) a document key with the master key
 *
 * The document key is exported to raw bytes, then encrypted with the master key.
 * Both the encrypted key and its IV are returned and should be stored with
 * the document metadata.
 *
 * @param documentKey - Document encryption key to wrap
 * @param masterKey - Master encryption key
 * @returns Wrapped key bundle with encrypted key and IV (both base64-encoded)
 */
export async function wrapDocumentKeyWithMaster(
  documentKey: CryptoKey,
  masterKey: CryptoKey,
): Promise<{ encryptedKey: string; keyIv: string }> {

  try {
    // Export document key to raw bytes
    const documentKeyBytes = await window.crypto.subtle.exportKey(
      'raw',
      documentKey,
    );

    // Generate random IV for key wrapping
    const keyIv = window.crypto.getRandomValues(new Uint8Array(12));

    // Ensure document key is backed by regular ArrayBuffer
    const keyBuffer = new ArrayBuffer(documentKeyBytes.byteLength);
    new Uint8Array(keyBuffer).set(new Uint8Array(documentKeyBytes));

    // Encrypt the document key with master key
    const encryptedKey = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: keyIv,
      },
      masterKey,
      keyBuffer,
    );


    return {
      encryptedKey: arrayBufferToBase64(encryptedKey),
      keyIv: arrayBufferToBase64(keyIv),
    };
  } catch (error) {
    logger.error('Failed to wrap document key', error as Error);
    throw new Error('Failed to wrap document key');
  }
}

/**
 * Unwrap (decrypt) a document key using the master key
 *
 * Reverses the wrapping process to recover the document encryption key.
 * The unwrapped key is imported as a CryptoKey ready for decryption operations.
 *
 * @param wrappedKey - Encrypted key bundle (base64-encoded)
 * @param keyIv - IV for key decryption (base64-encoded)
 * @param masterKey - Master encryption key
 * @returns Imported CryptoKey for document decryption
 */
export async function unwrapDocumentKeyWithMaster(
  wrappedKey: string,
  keyIv: string,
  masterKey: CryptoKey,
): Promise<CryptoKey> {

  try {
    const encryptedKeyArray = base64ToArrayBuffer(wrappedKey);
    const keyIvArray = base64ToArrayBuffer(keyIv);

    // Ensure buffers are backed by regular ArrayBuffer
    const keyBuffer = new ArrayBuffer(encryptedKeyArray.length);
    new Uint8Array(keyBuffer).set(encryptedKeyArray);

    const ivBuffer = new ArrayBuffer(keyIvArray.length);
    new Uint8Array(ivBuffer).set(keyIvArray);

    // Decrypt the document key
    const documentKeyBytes = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: new Uint8Array(ivBuffer),
      },
      masterKey,
      new Uint8Array(keyBuffer),
    );

    // Import the decrypted key material as a CryptoKey
    const documentKeyBuffer = new ArrayBuffer(documentKeyBytes.byteLength);
    new Uint8Array(documentKeyBuffer).set(new Uint8Array(documentKeyBytes));

    const documentKey = await window.crypto.subtle.importKey(
      'raw',
      documentKeyBuffer,
      {
        name: 'AES-GCM',
        length: 256,
      },
      false, // not extractable after unwrapping
      ['decrypt'],
    );

    return documentKey;
  } catch (error) {
    logger.error('Failed to unwrap document key', error as Error);
    throw new Error('Failed to unwrap document key');
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Derive encryption key from password using PBKDF2
 */
async function deriveKeyFromPassword(
  password: string,
  salt: Uint8Array | ArrayBuffer,
): Promise<CryptoKey> {
  const passwordBuffer = new TextEncoder().encode(password);

  const passwordKey = await window.crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  // Ensure salt is backed by a regular ArrayBuffer, not SharedArrayBuffer
  let saltBuffer: ArrayBuffer;
  if (salt instanceof ArrayBuffer && !(salt instanceof SharedArrayBuffer)) {
    saltBuffer = salt;
  } else {
    // Create a copy backed by a regular ArrayBuffer
    const saltArray = new Uint8Array(salt);
    const buffer = new ArrayBuffer(saltArray.length);
    new Uint8Array(buffer).set(saltArray);
    saltBuffer = buffer;
  }

  return await window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: 100000,
      hash: 'SHA-256',
    },
    passwordKey,
    {
      name: 'AES-GCM',
      length: 256,
    },
    false,
    ['encrypt', 'decrypt'],
  );
}

/**
 * Generate SHA-256 hash of recovery key for validation
 */
async function generateRecoveryKeyHash(
  recoveryPhrase: string,
): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(recoveryPhrase);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  return arrayBufferToBase64(hashBuffer);
}

/**
 * Convert ArrayBuffer to Base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i] ?? 0);
  }
  return btoa(binary);
}

/**
 * Convert Base64 string to ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  // Ensure the Uint8Array is backed by a regular ArrayBuffer, not SharedArrayBuffer
  return new Uint8Array(bytes);
}
